// src/components/app/Layout.tsx 2

import { Outlet, useLocation } from "react-router-dom";
import BottomNavBar from "../common/BottomNavBar/BottomNavBar";
import { useEffect, useRef, useState } from "react";
import { WorldAuthClient } from "@bridge-applications/world-auth";
import SignInContent from "../common/BottomDrawer/contents/SignInContent/SignInContent";
import { useSlice } from "@bridge-applications/provider-kit";
import { telemetry } from "@bridge-applications/telemetry-js";
import { getClientTz } from "../../logic/helpers/getClientTz";
import {
    ApiAuthAccessTokenResSchema,
    ApiAuthAppSessionReqSchema,
    ApiAuthAppSessionResSchema,
    ApiCheckInStatusReqSchema,
    ApiCheckInStatusResSchema,
    ApiGlobalRewardActivityResSchema,
} from "@bridge-applications/eh-types";
import { API_BASE, NS2 } from "../../state/config";
import { getSpotsLeftMapForFeed } from "../../logic/helpers/getSpotsLeftForFeed";
import { getPermissionStatuses } from "../../logic/helpers/getPermissionStatuses";
import { apiNotiUpdate } from "../../logic/apiNotiUpdate";
import { toast } from "react-hot-toast";
import { useTranslation } from "react-i18next";
import { BottomDrawer } from "@bridge-applications/ui-kit";
import {
    useStartupAnns,
    type UseStartupAnnsOptions,
} from "../../logic/helpers/startupAnns/useStartupAnns";
import StartupAnnDialog from "../common/StartupAnnDialog/StartupAnnDialog";
import DailyRewardDialog from "../common/DailyReward/DailyRewardDialog";
import { getDailyPromoItem } from "../../logic/helpers/dailyPromo/getDailyPromoItem";
import { useWbcStwClaimOrchestrator } from "../../logic/hooks/useWbcStwClaimOrchestrator";
import { useNotiClaimOrchestrator } from "../../logic/hooks/useNotiClaimOrchestrator";
import {
    captureRefv2IntentFromUrl,
    clearPendingRefv2Intent,
} from "../../logic/helpers/refv2Intent";

// Define the regex patterns for routes where the bottom navbar should be shown
const BOTTOM_NAV_PATTERNS = [
    /^\/$/, // Exact match for root "/"
    /^\/business$/, // Exact match for "/business"
    /^\/map$/, // Exact match for "/map"
    /^\/settings$/, // Exact match for "/settings"
];

type LayoutLocationState = {
    skipStartupAnns?: boolean;
    refreshSession?: boolean;
} | null;

// Stable UUID for client-only AB_TEST "item"
// const AB_TEST_ID = "04fca0e1-9928-419f-a320-e09b101d7a95";
// const GAE_ID = "7a801474-a8e9-443d-bb64-88db9f90bb64";

export default function Layout() {
    const [openBottomDrawer, setOpenBottomDrawer] = useState<"auth" | null>(
        null,
    );
    const { setSlice: setFeedSlice } = useSlice("feedSlice");
    const { state: appState, setSlice: setAppSlice } = useSlice("appSlice");
    const location = useLocation();
    const locationState = (location.state ?? null) as LayoutLocationState;
    const [shouldShowBottomNav, setShouldShowBottomNav] =
        useState<boolean>(false);
    const { t, i18n } = useTranslation();

    // Track whether the app-session load has completed at least once
    const [appSessionEverLoaded, setAppSessionEverLoaded] = useState(false);
    const lastAppSessionLoadingRef = useRef<boolean | undefined>(
        appState.loading?.appSession,
    );

    // Re-entrancy + idempotency guards for getSession().
    // - inFlightRef: prevents two concurrent getSession() calls (the Auth-driven
    //   useEffect can fire twice in quick succession when WorldAuthClient.isAuthenticated
    //   flips from false → true mid-bootstrap, and the location-driven useEffect
    //   can also forcibly trigger a refresh).
    // - bootstrappedRef: prevents the AUTHENTICATED branch of the auth effect from
    //   triggering a *second* full session refetch right after the EXPECTED branch's
    //   first call, which was clobbering newly-arrived dialog state (notably
    //   checkInVoucherAvailable / checkInRewards) for some users — see the
    //   DailyRewardDialog auto-close bug.
    //   Reset to false on UNAUTHENTICATED so a sign-out → sign-in flow refetches.
    const inFlightRef = useRef(false);
    const bootstrappedRef = useRef(false);

    useEffect(() => {
        const current = appState.loading?.appSession;
        const prev = lastAppSessionLoadingRef.current;

        // We only consider "loaded" once we've seen a transition from true -> false
        if (prev === true && current === false) {
            setAppSessionEverLoaded(true);
        }

        lastAppSessionLoadingRef.current = current;
    }, [appState.loading?.appSession]);

    // Startup announcements: only enabled AFTER app-session has completed once
    // const { activeAnn, dismissActiveAnn } = useStartupAnns({
    //     noneWeight: 0,
    //     enabled: appSessionEverLoaded,
    // });

    // Startup announcements: only enabled AFTER app-session has completed once,
    // and only when the current navigation did NOT request skipping them.

    const startupConfig: UseStartupAnnsOptions = {
        noneWeight: 0,
        enabled: appSessionEverLoaded && !locationState?.skipStartupAnns,
    };

    console.log("startupConfig", startupConfig);

    const { activeAnn, dismissActiveAnn } = useStartupAnns(startupConfig);
    console.log("activeAnn", activeAnn);

    useWbcStwClaimOrchestrator({
        appSessionLoading: appState.loading?.appSession,
    });

    useNotiClaimOrchestrator({
        appSessionLoading: appState.loading?.appSession,
    });

    // Error codes that are *expected* during normal auth flow and should NOT be thrown
    // (everything else is treated as unexpected and rethrown so Sentry can capture)
    const EXPECTED_APPSESSION_ERROR_CODES = new Set([
        "UNAUTHENTICATED",
        "REFRESH_TOKEN_REQUIRED",
        "REFRESH_TOKEN_EXPIRED",
        "MISSING_REFRESH_TOKEN",
        "USER_BANNED",
        "USER_NOT_FOUND",
        "INVALID_REFRESH_TOKEN",
    ]);
    // Check if current URL matches any of the bottom nav patterns
    const checkBottomNavVisibility = () => {
        const currentPath = location.pathname;
        const shouldShow = BOTTOM_NAV_PATTERNS.some((pattern) =>
            pattern.test(currentPath),
        );
        setShouldShowBottomNav(shouldShow);
    };

    // Check bottom nav visibility whenever the location changes
    // Moved to after getSession definition to allow calling it
    // useEffect(() => {
    //     checkBottomNavVisibility();
    // }, [location]);

    const getSession = async () => {
        // Re-entrancy guard: collapse concurrent calls into one.
        // Without this, the auth-state useEffect can fire a second getSession()
        // while the first is still in flight, and the second response can
        // overwrite (null out) checkInVoucherAvailable / checkInRewards while
        // the DailyRewardDialog is open, causing it to disappear unexpectedly.
        if (inFlightRef.current) {
            console.log("[Layout] getSession: skipping, already in-flight");
            return;
        }
        inFlightRef.current = true;
        setAppSlice({
            loading: {
                appSession: true,
                submittingTask: false,
                wheelLoading: false,
            },
        });
        // Kick off check-in status call *concurrently* with the app-session call.
        // We await it just before writing the feed to include the card.
        let checkInStatusPromise: Promise<any> | null = null;
        let globalRewardActivityPromise: Promise<any> | null = null;
        try {
            const { tz } = await getClientTz();
            // Validate req body with Zod to keep parity with server schema
            const checkInReq = ApiCheckInStatusReqSchema.parse({
                timezone: tz,
            });
            checkInStatusPromise = fetch(`${API_BASE}/api/v1/check-in/status`, {
                method: "POST",
                credentials: "include", // send cookies (refresh token)
                headers: {
                    "Content-Type": "application/json",
                    Accept: "application/json",
                },
                body: JSON.stringify(checkInReq),
            })
                .then((r) => r.json())
                .catch((e) => ({
                    status: "ERROR",
                    error: e?.message || "Network error",
                }));

            // Fetch global reward activity *in parallel* with the app-session call.
            // This endpoint is read-only and can resolve independently of auth.
            globalRewardActivityPromise = fetch(
                `${API_BASE}/api/v1/global/reward-activity`,
                {
                    method: "POST",
                    credentials: "include",
                    headers: {
                        Accept: "application/json",
                    },
                },
            )
                .then((r) => r.json())
                .catch((e) => ({
                    status: "ERROR",
                    error: e?.message || "Network error",
                }));

            const refv2Intent = captureRefv2IntentFromUrl();
            const appSessionReq = ApiAuthAppSessionReqSchema.parse({
                timezone: tz,
                locale: i18n.language,
                ...(refv2Intent ? { refv2Intent } : {}),
            });
            const _response =
                await WorldAuthClient.getAppSession<any>(appSessionReq);

            const s: any = ApiAuthAppSessionResSchema;

            // Zod v4 stores the object "shape" here
            const def = s?._zod?.def ?? s?._def;
            const shape = def?.shape
                ? typeof def.shape === "function"
                    ? def.shape()
                    : def.shape
                : null;

            console.log(
                "[debug-001] ApiAuthAppSessionResSchema keys:",
                shape ? Object.keys(shape) : shape,
            );
            console.log("[debug-001] feed schema:", shape?.feed);
            console.log(
                "[debug-001] feed element type:",
                shape?.feed?._zod?.def?.element?._zod?.def?.type ??
                    shape?.feed?._def?.type,
            );

            const response = ApiAuthAppSessionResSchema.parse(_response);

            console.log("[debug-001] response", response);

            if (
                response.refv2Attribution?.status === "OPENED" ||
                response.refv2Attribution?.status === "SKIPPED"
            ) {
                clearPendingRefv2Intent();
            }

            if (response.status === "OK") {
                /* --------------------------------------------------------- *
                 * 1) Spots-left map for feed items (single Redis mget)
                 * --------------------------------------------------------- */
                const spotsLeftMap = await getSpotsLeftMapForFeed(
                    response.feed,
                );
                console.log("spotsLeftMap(feed)", spotsLeftMap);

                /* --------------------------------------------------------- *
                 * 2) Attach spotsLeft to each feed item
                 * --------------------------------------------------------- */
                const itemsWithSpotsLeft = response.feed.map((it) => ({
                    ...it,
                    spotsLeft:
                        spotsLeftMap[`${it.productType}:${it.itemId}`] ?? null,
                }));

                /* --------------------------------------------------------- *
                 * 2b) Merge BRIDGE_CHECK_IN feed item (from /check-in/status)
                 *     Await the concurrent promise *here* to keep UX snappy.
                 * --------------------------------------------------------- */
                let itemsFinal = itemsWithSpotsLeft;
                if (checkInStatusPromise) {
                    const raw = await checkInStatusPromise;
                    const parsed = ApiCheckInStatusResSchema.safeParse(raw);
                    if (parsed.success && parsed.data.status === "OK") {
                        const unDailyItem = getDailyPromoItem();

                        itemsFinal = [
                            unDailyItem as any,
                            ...itemsWithSpotsLeft,
                        ];
                    } else if (
                        parsed.data?.status === "ERROR" &&
                        parsed.data.error_code === "USER_NOT_FOUND"
                    ) {
                        const unDailyItem = getDailyPromoItem();

                        itemsFinal = [
                            unDailyItem as any,
                            ...itemsWithSpotsLeft,
                        ];
                    } else {
                        console.warn(
                            "[/api/v1/check-in/status] not OK or invalid:",
                            parsed,
                        );
                    }
                }

                /* --------------------------------------------------------- *
                 * 3) Persist to the slice (schema now uses items/*Count)
                 * --------------------------------------------------------- */
                setFeedSlice({
                    items: itemsFinal,
                    returnedItemCount: response.returnedItemCount,
                    totalItemCount: response.totalItemCount,
                    nextCursor: response.nextCursor ?? null,
                });
                console.log("Feed successfully written to the feedSlice.");

                // Telemetry: identify and capture authenticated user
                telemetry.identify(response.userId, {
                    walletAddress: response.walletAddress,
                });
                telemetry.capture("auth__user_authenticated");

                setAppSlice({
                    checkInVoucherAvailable: response.checkInVoucher
                        ? {
                              unit: response.checkInVoucher.unit,
                              amount: response.checkInVoucher.amount,
                              nonce: response.checkInVoucher.nonce,
                              signature: response.checkInVoucher.signature,
                          }
                        : null,
                    checkInRewards:
                        response.checkInRewards &&
                        response.checkInRewards.length > 0
                            ? response.checkInRewards.map((r) => ({
                                  tokenAddress: r.tokenAddress,
                                  amount: r.amount,
                                  label: r.label,
                                  voucher: {
                                      struct: {
                                          to: r.voucher.struct.to,
                                          tokenType: r.voucher.struct.tokenType,
                                          token: r.voucher.struct.token,
                                          amount: r.voucher.struct.amount,
                                          tokenId: r.voucher.struct.tokenId,
                                          expiresAt: r.voucher.struct.expiresAt,
                                          nonce: r.voucher.struct.nonce,
                                      },
                                      signature: r.voucher.signature,
                                      domain: {
                                          name: String(
                                              (r.voucher.domain as any).name ??
                                                  "",
                                          ),
                                          version: String(
                                              (r.voucher.domain as any)
                                                  .version ?? "",
                                          ),
                                          chainId: Number(
                                              (r.voucher.domain as any)
                                                  .chainId ?? 0,
                                          ),
                                          verifyingContract: String(
                                              (r.voucher.domain as any)
                                                  .verifyingContract ?? "",
                                          ),
                                      },
                                  },
                              }))
                            : null,
                    streak: {
                        currentCount: response.streak.currentCount,
                        topCount: response.streak.topCount,
                        mostRecent14Ts: response.streak.mostRecent14Ts,
                    },
                    checkInSchedule: response.checkInSchedule,
                    totalTasksCompleted: response.totalTasksCompleted,
                    totalRewardsEarned: response.totalRewardsEarned,
                    qaScore: response.qaScore,
                });

                localStorage.setItem(
                    `${NS2}rateVoucherReceived`,
                    response.rateVoucherReceived.toString(),
                );

                localStorage.setItem(
                    `${NS2}notiVoucherReceived`,
                    response.notiVoucherReceived.toString(),
                );

                // GET ALL COUNTERS

                setAppSlice({
                    loading: {
                        appSession: false,
                        submittingTask: false,
                        wheelLoading: false,
                    },
                });
            } else {
                const code = (response as any).code as string | undefined;
                const message = (response as any).message as string | undefined;
                console.warn("App session indicates auth required / error:", {
                    code,
                    message,
                });
                // Clear any stale access token locals written by the client lib
                localStorage.removeItem("worldAuth_accessToken");
                localStorage.removeItem("worldAuth_accessTokenExpiresAt");
                // If banned, keep drawer closed; surface a message instead.
                if (code === "USER_BANNED") {
                    toast.error(
                        t("errors.userBanned") || "Your account is banned.",
                    );
                    setOpenBottomDrawer(null);
                } else {
                    // Do NOT clear refresh expiry timestamp; server is source of truth
                    setOpenBottomDrawer("auth");
                }
                setAppSlice({
                    loading: {
                        appSession: false,
                        submittingTask: false,
                        wheelLoading: false,
                    },
                });

                // Re-throw only if it's NOT one of the expected auth flow codes.
                if (!EXPECTED_APPSESSION_ERROR_CODES.has(code ?? "")) {
                    throw new Error(
                        `[AppSession] Unexpected error${
                            code ? ` (${code})` : ""
                        }: ${message ?? "No message"}`,
                    );
                }
            }
        } catch (e) {
            console.error("getSession failed", e);
            setOpenBottomDrawer("auth");
            setAppSlice({
                loading: {
                    appSession: false,
                    submittingTask: false,
                    wheelLoading: false,
                },
            });
            // Re-throw so Sentry (or global handlers) can capture unexpected failures
            throw e;
        } finally {
            // Regardless of the app-session outcome, persist what we got (if anything)
            if (globalRewardActivityPromise) {
                try {
                    const rawGlobal = await globalRewardActivityPromise;
                    const parsedGlobal =
                        ApiGlobalRewardActivityResSchema.safeParse(rawGlobal);
                    if (
                        parsedGlobal.success &&
                        parsedGlobal.data.status === "OK"
                    ) {
                        setAppSlice({
                            globalRewardActivity: parsedGlobal.data.data,
                        });
                    } else {
                        // Keep it predictable for the UI
                        setAppSlice({
                            globalRewardActivity: null,
                        });
                        console.warn(
                            "[/api/v1/global/reward-activity] not OK or invalid:",
                            parsedGlobal,
                        );
                    }
                } catch (err) {
                    console.warn(
                        "[/api/v1/global/reward-activity] failed:",
                        err,
                    );
                    setAppSlice({ globalRewardActivity: null });
                }
            } else {
                setAppSlice({ globalRewardActivity: null });
            }
            // Always release the in-flight guard, even if the global-reward-activity
            // post-processing or the main flow threw — otherwise getSession would
            // be wedged for the rest of the session.
            inFlightRef.current = false;
        }
    };

    // Check bottom nav visibility whenever the location changes
    useEffect(() => {
        checkBottomNavVisibility();
        const capturedRefv2Intent = captureRefv2IntentFromUrl();
        const state = (location.state ?? null) as LayoutLocationState;
        if (state?.refreshSession || capturedRefv2Intent) {
            // Intentional forced refetch (e.g. user came back from a flow that
            // mutated server state). The inFlightRef guard inside getSession()
            // collapses this with any in-progress bootstrap call so we never
            // race two concurrent app-session requests.
            getSession().catch((e) => {
                console.error("[Layout] getSession (refreshSession) failed", e);
            });
        }
    }, [location]);

    const updateNotiStatus = async (notiStatus: boolean) => {
        try {
            const tokenRaw = await WorldAuthClient.getAccessToken();
            const tokenRes = ApiAuthAccessTokenResSchema.parse(tokenRaw);
            // tokenRes conforms to ApiAuthAccessTokenResSchema
            if (tokenRes.status !== "OK" || !tokenRes.accessToken) {
                console.warn(
                    "Cannot update noti status without access token",
                    tokenRes,
                );
                setOpenBottomDrawer("auth");
                // Treat missing/invalid token as expected auth flow; don't throw here.
                return;
            }
            const res = await apiNotiUpdate(notiStatus, tokenRes.accessToken);
            console.log("res", res);
            if (res.status === "OK") {
                localStorage.setItem(
                    `${NS2}notificationsEnabled`,
                    notiStatus.toString(),
                );
            } else {
                toast.error(
                    "Failed to update notification status. Please refresh the app!.",
                );
                // Unexpected server error from our API call — throw for Sentry
                throw new Error(
                    `[NotiUpdate] Unexpected response status: ${res.status}`,
                );
            }
        } catch (e) {
            // Surface to Sentry/global handlers
            console.error("updateNotiStatus failed", e);
            throw e;
        }
    };

    useEffect(() => {
        console.log("---CHECKING---");

        if (WorldAuthClient.isInitialized()) {
            const accessToken = localStorage.getItem(`worldAuth_accessToken`);
            const accessTokenExpiresAt = localStorage.getItem(
                `worldAuth_accessTokenExpiresAt`,
            );
            const refreshTokenExpiresAt = localStorage.getItem(
                `worldAuth_refreshTokenExpiresAt`,
            );
            console.log("accessToken", accessToken);
            console.log("accessTokenExpiresAt", accessTokenExpiresAt);
            console.log("refreshTokenExpiresAt", refreshTokenExpiresAt);
            console.log(
                "WorldAuthClient.isAuthenticated",
                WorldAuthClient.isAuthenticated,
            );
            console.log(
                "WorldAuthClient.expectedToBeAuthenticated",
                WorldAuthClient.expectedToBeAuthenticated,
            );

            if (
                !WorldAuthClient.isAuthenticated &&
                !WorldAuthClient.expectedToBeAuthenticated
            ) {
                console.log("UNAUTHENTICATED");
                // User is signed out → reset bootstrap so a future sign-in
                // (which flips isAuthenticated → true and re-runs this effect)
                // performs a fresh getSession(). Without this reset we'd skip
                // the post-sign-in fetch and the user would be stuck without
                // a hydrated session.
                bootstrappedRef.current = false;
                setOpenBottomDrawer("auth");
            } else if (
                !WorldAuthClient.isAuthenticated &&
                WorldAuthClient.expectedToBeAuthenticated
            ) {
                console.log("EXPECTED TO BE AUTHENTICATED");
                const username = localStorage.getItem(`${NS2}worldUsername`);
                const profilePictureUrl = localStorage.getItem(
                    `${NS2}worldProfilePictureUrl`,
                );
                const walletAddress = localStorage.getItem(
                    `${NS2}walletAddress`,
                );
                setAppSlice({
                    username: username ?? null,
                    profilePictureUrl: profilePictureUrl ?? null,
                    walletAddress: walletAddress ?? null,
                });
                if (!bootstrappedRef.current) {
                    bootstrappedRef.current = true;
                    getSession().catch((e) => {
                        console.error(
                            "[Layout] getSession (EXPECTED) failed",
                            e,
                        );
                        // Allow retry on the next state transition
                        // (e.g. when isAuthenticated flips true).
                        bootstrappedRef.current = false;
                    });
                }
            } else if (
                (WorldAuthClient.isAuthenticated &&
                    !WorldAuthClient.expectedToBeAuthenticated) ||
                (WorldAuthClient.isAuthenticated &&
                    WorldAuthClient.expectedToBeAuthenticated)
            ) {
                console.log("AUTHENTICATED");
                const username = localStorage.getItem(`${NS2}worldUsername`);
                const profilePictureUrl = localStorage.getItem(
                    `${NS2}worldProfilePictureUrl`,
                );
                const walletAddress = localStorage.getItem(
                    `${NS2}walletAddress`,
                );
                setAppSlice({
                    username: username ?? null,
                    profilePictureUrl: profilePictureUrl ?? null,
                    walletAddress: walletAddress ?? null,
                });
                setOpenBottomDrawer(null);
                // Skip the refetch when we already bootstrapped — typically
                // this branch fires immediately after EXPECTED's getSession()
                // landed an access token, flipping isAuthenticated true and
                // re-running this effect. Without the guard we'd refetch the
                // session and clobber freshly-arrived dialog state (notably
                // checkInVoucherAvailable / checkInRewards), which closed
                // the DailyRewardDialog without user interaction.
                if (!bootstrappedRef.current) {
                    bootstrappedRef.current = true;
                    getSession().catch((e) => {
                        console.error(
                            "[Layout] getSession (AUTHENTICATED) failed",
                            e,
                        );
                        bootstrappedRef.current = false;
                    });
                }
            }
        }

        const _notificationsEnabled: string | null = localStorage.getItem(
            `${NS2}notificationsEnabled`,
        );
        const notificationsEnabled: boolean | null =
            _notificationsEnabled !== null
                ? _notificationsEnabled === "true"
                : null;

        console.log("RAW NOTIS ENABLED", notificationsEnabled);
        if (notificationsEnabled === null) {
            getPermissionStatuses().then((res) => {
                updateNotiStatus(res.notiStatus);
                setAppSlice({
                    notificationsEnabled: res.notiStatus,
                });
            });
        } else {
            getPermissionStatuses().then(async (res) => {
                console.log("DEBUG", res.notiStatus, notificationsEnabled);
                if (res.notiStatus !== notificationsEnabled) {
                    console.log(
                        "NOTI STATUS CHANGED, FROM",
                        notificationsEnabled,
                        "TO",
                        res.notiStatus,
                    );
                    updateNotiStatus(res.notiStatus);
                }
                setAppSlice({
                    notificationsEnabled: res.notiStatus,
                });
            });
        }
    }, [
        WorldAuthClient.isAuthenticated,
        WorldAuthClient.expectedToBeAuthenticated,
    ]);

    return (
        <div id="layout" style={{ height: "100svh", position: "relative" }}>
            <main
                className="main-content"
                style={{
                    height: "100svh",
                    position: "relative",
                    // overflowY: "auto",
                    // overflowX: "hidden",
                    WebkitOverflowScrolling: "touch",
                }}
            >
                <Outlet />
            </main>
            {shouldShowBottomNav && <BottomNavBar />}

            <BottomDrawer
                isOpen={openBottomDrawer === "auth"}
                canClose={false}
                onClose={() => {}}
                title={t("Layout.signIn")}
                className="auth-bottom-drawer"
                withBackdrop={true}
            >
                <SignInContent
                    onSuccess={() => {
                        setOpenBottomDrawer(null);
                    }}
                />
            </BottomDrawer>
            {/* Startup announcement dialog (shown after app-session has loaded) */}
            <StartupAnnDialog
                ann={activeAnn}
                onClose={() => {
                    dismissActiveAnn();
                }}
            />
            <DailyRewardDialog
                open={
                    appState.checkInVoucherAvailable !== null ||
                    (appState.checkInRewards?.length ?? 0) > 0
                }
                // open={true} // TODO! TEMP
                onClose={() => {
                    setAppSlice({
                        checkInVoucherAvailable: null,
                        checkInRewards: null,
                    });
                }}
            />
        </div>
    );
}
